import { useState, useEffect } from 'react';

import DataGrid from 'app/components/DataGrid';
import messages, { FormattedMessage, withValues } from 'shared/messages/messages';
import useJobConfigs from 'app/hooks/useJobConfigs';
import JobConfigModal from './JobConfigModal';
import classes from './classes.module.scss';

import {
  getJobConfigTemplates
} from 'app/services/jobConfigTemplates';

import {
  formatJobConfigCellValue
} from 'app/utils/valueFormatters';

import {
  createOption,
  formatOptions
} from 'app/utils/options';

import {
  getCurrencySign
} from 'shared/services/PriceService';

const JobConfigs = props => {
  // load needed resources on first render
  const [jobConfigTemplates, setJobConfigTemplates] = useState([]);
  useEffect(() => {
    getJobConfigTemplates({ page: 1, per_page: 100 })
      .then(resp => setJobConfigTemplates(resp.records));
  }, []);

  const {
    resource,
    formIsOpen,
    currentRecord,
    onFormClose,
    onAdd,
    onEdit,
    onQuickAdd,
    onQuickEdit,
    onDataGridLoad,
    onFormSubmit,
    onDestroy,
    showFilters,
    getRowData
  } = useJobConfigs();

  const pricingModelOptions = [
    createOption('Basic', 0, {
      helperText: 'Set a price per moment and a price for all'
    }),
    createOption('Volume', 2, {
      helperText: 'Set volume-based discounts'
    }),
    createOption('Packages', 3, {
      helperText: 'Setup quantity-based packages'
    })
  ]

  const header = 'Products';
  const addButtonLabel = 'Add Product';
  const columnOrder = ['name', 'pricing_model', 'price', 'discount', 'pricing_tiers', 'description'];
  const visibleColumns = [].concat(columnOrder);

  const omitColumns = [
    'notification_template', // crashes React
    'product' // crashes React
  ];

  // need to figure out how to load jobConfigTemplates
  const jobConfigTemplateOptions = formatOptions(jobConfigTemplates);

  const formatCurrency = (amount, currency) => {
    currency ||= 'USD';
    let symbol = getCurrencySign(currency);
    let text = `${symbol}${amount ?? '0.00'} ${currency}`;

    return <span className={classes.cell}>{text}</span>;
  }

  const columnDefOverrides = [
    { field: 'created_at', formatType: 'date' },
    { field: 'updated_at', formatType: 'date' },
    {
      field: 'name',
      quickEdit: true,
      renderFields: ['name', 'template_name']
    },
    //{ field: 'name', style: { fontWeight: 500 } },
    {
      field: 'template_name',
      headerName: 'Product Type',
      inputType: 'select',
      inputOptions: jobConfigTemplateOptions,
      quickEdit: true
    },
    {
      field: 'pricing_model',
      cellRenderer: params => {
        let data = params.data;
        if (data) {
          let key = `${data.id}-pricing_model`;

          let text;
          if (data.id === -1) {
            // Digital Delivery, hard code label
            text = 'None';
          } else {
            let pricingModel = pricingModelOptions.find(o => {
              return o.value === data.product.pricing_model;
            });
            text = pricingModel?.label;
          }

          return <span key={key} className={classes.cell}>{text}</span>;
        }
        return '';
      },
      inputType: 'select',
      inputOptions: pricingModelOptions,
      quickEdit: true
    },
    {
      field: 'price',
      headerName: 'Price per image',
      cellRenderer: params => {
        let data = params.data;
        if (data) {
          return formatCurrency(data.product.price, data.currency);
        }
        return '';
      },
      inputType: 'number'
    },
    {
      field: 'discount',
      headerName: 'Price for all',
      cellRenderer: params => {
        let data = params.data;
        if (data) {
          return formatCurrency(data.product.discount, data.currency);
        }
        return '';
      },
      inputType: 'number'
    },
    {
      field: 'pricing_tiers',
      headerName: 'Packages',
      cellRenderer: params => {
        let data = params.data;

        if (data && data.discounts) {
          let key = `${data.id}-pricing_tiers`;

          let packages = data.discounts.map(({ message: { id, values } }, i) => ((
            <li key={key + i} className={classes.packagesListItem}>
              <FormattedMessage {...withValues(messages[id], values)} />
            </li>
          )));

          return (
            <div key={key} className={classes.cell}>
              <ul className={classes.packagesList}>
                {packages}
              </ul>
            </div>
          );
        }
        return '';
      },
      filterDisabled: true
    }
  ]

  const callback = selectedRows => {
    console.log(selectedRows);
  }

  const actions = [
    { key: 'destroy', label: 'Delete Products', callback }
  ]

  return (
    <div>
      <DataGrid
        resource={resource}
        header={header}
        getRowData={getRowData}
        addButtonLabel={addButtonLabel}
        onAdd={onAdd}
        showEdit={true}
        onEdit={onEdit}
        columnOrder={columnOrder}
        visibleColumns={visibleColumns}
        omitColumns={omitColumns}
        columnDefOverrides={columnDefOverrides}
        loadingMessage="Loading products..."
        showSelect={true}
        actions={actions}
        showFilters={showFilters}
        onDataGridLoad={onDataGridLoad}
      />
      <JobConfigModal
        isOpen={formIsOpen}
        onClose={onFormClose}
        jobConfig={currentRecord}
        jobConfigTemplates={jobConfigTemplates}
        onFormSubmit={onFormSubmit}
        onDestroy={onDestroy}
      />
    </div>
  );
}

export default JobConfigs;
